import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import * as apis from "../../network/apis/requests";
import messages from "../../assets/locale/messages";
import {
  getEventRequestListMeta,
  normalizeEventRequests,
  extractCheckedInSummaryFromResponse,
  isEventRequestTabCountParams,
} from "../../components/Events/Event/Applicants/eventRequestMappers";
import {
  joiningRequestApiStatuses,
  joiningRequestRejectedApiStatuses,
} from "../../components/Events/Event/Applicants/constants";
import {
  showErrorMsg,
  showSuccessMsg,
  showWarningMsg,
} from "../../utils/Helpers";
import { getEventRequest } from "../Events/actions";
import * as actions from "./actions";
import * as types from "./types";

const extractListItems = (response) => response?.data?.data ?? [];
const normalizeIds = (ids = []) =>
  ids.map((id) => {
    const numberId = Number(id);
    return Number.isFinite(numberId) ? numberId : id;
  });
const isBulkOperationPartialSuccess = (response) => response?.status === 207;

function* refreshAdminEventRequests(listParams) {
  if (!listParams) return;
  yield put(actions.getAdminEventRequestsRequest(listParams));
}

function* refreshAdminEvent(listParams) {
  if (!listParams?.event_id) return;
  yield put(getEventRequest(listParams.event_id));
}

function* getAdminEventRequestsSaga({ payload }) {
  try {
    const canUpdateTabCount = isEventRequestTabCountParams(payload);

    yield put(
      actions.getAdminEventRequestsResponse({
        list: [],
        meta: { currentPage: 1, count: 0, pages: 1 },
      }),
    );
    const response = yield call(apis.getRequestsList, payload);
    const lang = yield select((state) => state.locale.lang);
    const sharedLabels = messages[lang]?.shared;
    const meta = getEventRequestListMeta(response);
    const list = normalizeEventRequests(
      extractListItems(response),
      lang,
      sharedLabels,
    );
    yield put(
      actions.getAdminEventRequestsResponse({
        list,
        meta,
      }),
    );
    if (
      payload?.status === joiningRequestApiStatuses.pending &&
      canUpdateTabCount
    ) {
      yield put(
        actions.getAdminEventRequestsSummaryResponse({
          eventId: payload.event_id,
          pending: meta.count,
        }),
      );
    } else if (payload?.status === joiningRequestApiStatuses.joined) {
      const checkInSummary = extractCheckedInSummaryFromResponse(response, list);
      const summaryPatch = { eventId: payload.event_id };

      if (canUpdateTabCount) {
        summaryPatch.joined = meta.count;
      }
      if (checkInSummary.hasExplicitCounts) {
        summaryPatch.checkedIn = checkInSummary.checkedIn;
        summaryPatch.notCheckedIn = checkInSummary.notCheckedIn;
      }
      if (Object.keys(summaryPatch).length > 1) {
        yield put(actions.getAdminEventRequestsSummaryResponse(summaryPatch));
      }
    } else if (
      payload?.status === joiningRequestRejectedApiStatuses &&
      canUpdateTabCount
    ) {
      yield put(
        actions.getAdminEventRequestsSummaryResponse({
          eventId: payload.event_id,
          rejected: meta.count,
        }),
      );
    } else if (
      payload?.status === joiningRequestApiStatuses.removed &&
      canUpdateTabCount
    ) {
      yield put(
        actions.getAdminEventRequestsSummaryResponse({
          eventId: payload.event_id,
          removed: meta.count,
        }),
      );
    }
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
  }
}

function* bulkApproveAdminEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkApproveAdminEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      ...(payload.roleId ? { role_id: payload.roleId } : {}),
      ...(payload.eventRoleShiftId
        ? { event_role_shift_id: payload.eventRoleShiftId }
        : {}),
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshAdminEventRequests, payload.listParams);
      if (payload.successTitle && payload.successBody) {
        yield call(refreshAdminEvent, payload.listParams);
      }
      payload.onSuccess?.(response);
      return;
    }
    if (payload.successTitle && payload.successBody) {
      yield showSuccessMsg(payload.successBody, true, payload.successTitle);
    } else if (successMessage) {
      yield showSuccessMsg(successMessage, true);
    } else {
      yield showSuccessMsg("approveRequest");
    }
    yield call(refreshAdminEventRequests, payload.listParams);
    if (payload.successTitle && payload.successBody) {
      yield call(refreshAdminEvent, payload.listParams);
    }
    payload.onSuccess?.();
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkRejectAdminEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkRejectEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      rejection_reason: payload.rejectionReason,
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshAdminEventRequests, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshAdminEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkAllocateAdminEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkAllocateAdminEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      zone_id: payload.zoneId,
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshAdminEventRequests, payload.listParams);
      yield call(refreshAdminEvent, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshAdminEventRequests, payload.listParams);
    yield call(refreshAdminEvent, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkUnassignAdminEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkUnassignAdminEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshAdminEventRequests, payload.listParams);
      yield call(refreshAdminEvent, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshAdminEventRequests, payload.listParams);
    yield call(refreshAdminEvent, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkRemoveAdminEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkRemoveAdminEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      ...(payload.removalReason ? { removal_reason: payload.removalReason } : {}),
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshAdminEventRequests, payload.listParams);
      yield call(refreshAdminEvent, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshAdminEventRequests, payload.listParams);
    yield call(refreshAdminEvent, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* bulkChangeRoleAdminEventRequestsSaga({ payload }) {
  try {
    const response = yield call(apis.bulkChangeRoleAdminEventRequests, {
      event_request_ids: normalizeIds(payload.eventRequestIds),
      role_id: payload.roleId,
      event_role_shift_id: payload.eventRoleShiftId,
    });
    const successMessage = response.data?.message;
    if (isBulkOperationPartialSuccess(response)) {
      yield showWarningMsg(successMessage);
      yield call(refreshAdminEventRequests, payload.listParams);
      payload.onSuccess?.(response);
      return;
    }
    yield showSuccessMsg(successMessage, true);
    yield call(refreshAdminEventRequests, payload.listParams);
    payload.onSuccess?.(response);
  } catch (error) {
    yield showErrorMsg(error.response?.data?.message);
    payload.onError?.(error);
  }
}

export default function* adminEventRequestsSagas() {
  yield takeLatest(
    types.GET_ADMIN_EVENT_REQUESTS_REQUEST,
    getAdminEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_APPROVE_ADMIN_EVENT_REQUESTS_REQUEST,
    bulkApproveAdminEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_REJECT_ADMIN_EVENT_REQUESTS_REQUEST,
    bulkRejectAdminEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_ALLOCATE_ADMIN_EVENT_REQUESTS_REQUEST,
    bulkAllocateAdminEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_UNASSIGN_ADMIN_EVENT_REQUESTS_REQUEST,
    bulkUnassignAdminEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_REMOVE_ADMIN_EVENT_REQUESTS_REQUEST,
    bulkRemoveAdminEventRequestsSaga,
  );
  yield takeEvery(
    types.BULK_CHANGE_ROLE_ADMIN_EVENT_REQUESTS_REQUEST,
    bulkChangeRoleAdminEventRequestsSaga,
  );
}

